import { useState } from "react";
import { CloseOutlined, ClockCircleOutlined, LoadingOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";

export default function DeliveryTimeAdjuster({ currentDeliveryTime, onClose, onUpdate, isUpdating }) {
  const [adjustment, setAdjustment] = useState(0); // Minutes to add/subtract

  console.log("checking adjustment", adjustment);

  const handleIncrement = () => {
    setAdjustment(prev => prev + 5);
  };

  const handleDecrement = () => {
    setAdjustment(prev => prev - 5);
  };

  const handleReset = () => {
    setAdjustment(0);
  };

  const handleUpdate = () => {
    if (adjustment !== 0) {
      onUpdate(adjustment);
    }
  };

  // Calculate new time in minutes
  const getNewDeliveryTime = () => {
    return currentDeliveryTime + adjustment;
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" 
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 py-4 border-b">
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-900 text-white transition-all hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloseOutlined style={{ fontSize : '16px'}} />
          </button>

          <div className="pr-10">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ClockCircleOutlined className="text-gray-600" />
              Adjust Delivery Time
            </h2>
            <p className="text-sm text-gray-600 mt-1">Modify delivery time in 5-minute intervals</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Current Time Display */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-50 border-2 border-gray-200 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-1 mb-2">
                <ClockCircleOutlined className="text-gray-600 text-sm" />
                <p className="text-xs mt-1 font-semibold text-gray-600 uppercase tracking-wide">
                  Current Time
                </p>
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {currentDeliveryTime}
                <span className="text-lg font-normal text-gray-600 ml-1">min</span>
              </p>
            </div>

            <div className={`rounded-xl p-4 text-center transition-all duration-300 ${
              adjustment !== 0 
                ? 'bg-gradient-to-br from-gray-50 to-gray-50 border-2 border-gray-200' 
                : 'bg-slate-50 border-2 border-slate-200'
            }`}>
              <div className="flex items-center justify-center gap-1 mb-2">
                <ClockCircleOutlined className={adjustment !== 0 ? 'text-gray-600' : 'text-slate-400'} />
                <p className={`text-xs mt-0.5 font-semibold uppercase tracking-wide ${
                  adjustment !== 0 ? 'text-gray-600' : 'text-slate-400'
                }`}>
                  New Time
                </p>
              </div>
              <p className={`text-3xl font-bold ${adjustment !== 0 ? 'text-gray-900' : 'text-slate-400'}`}>
                {adjustment !== 0 ? getNewDeliveryTime() : currentDeliveryTime}
                <span className={`text-lg font-normal ml-1 ${adjustment !== 0 ? 'text-gray-600' : 'text-slate-400'}`}>
                  min
                </span>
              </p>
            </div>
          </div>

          {/* Adjustment Controls */}
          <div className="space-y-4">
            {/* Increment/Decrement Buttons */}
            <div className="flex items-center justify-center gap-6">
              <button
                onClick={handleDecrement}
                disabled={isUpdating}
                className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              >
                <MinusOutlined style={{ fontSize : '22px'}} className="group-hover:scale-110 transition-transform" />
              </button>

              {/* Adjustment Display */}
              <div className="flex-1 max-w-[140px] text-center">
                <div className={`text-5xl font-bold transition-colors ${
                  adjustment > 0 ? 'text-green-600' : 
                  adjustment < 0 ? 'text-red-600' : 
                  'text-slate-400'
                }`}>
                  {adjustment > 0 ? '+' : ''}{adjustment}
                </div>
                <p className="text-xs text-slate-500 mt-1 font-medium uppercase tracking-wide">
                  Minutes
                </p>
              </div>

              <button
                onClick={handleIncrement}
                disabled={isUpdating}
                className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              >
                <PlusOutlined style={{ fontSize : '22px'}} className="group-hover:scale-110 transition-transform" />
              </button>
            </div>

            {/* Quick Presets */}
            <div className="flex gap-2 justify-center flex-wrap">
              <button
                onClick={() => setAdjustment(-15)}
                disabled={isUpdating}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                -15 min
              </button>
              <button
                onClick={() => setAdjustment(-10)}
                disabled={isUpdating}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                -10 min
              </button>
              <button
                onClick={handleReset}
                disabled={isUpdating || adjustment === 0}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                Reset
              </button>
              <button
                onClick={() => setAdjustment(10)}
                disabled={isUpdating}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
              >
                +10 min
              </button>
              <button
                onClick={() => setAdjustment(15)}
                disabled={isUpdating}
                className="px-4 py-2 text-xs font-semibold rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
              >
                +15 min
              </button>
            </div>

            {/* Status Message */}
            {adjustment !== 0 && (
              <div className={`p-3 rounded-lg text-center text-sm font-medium animate-in fade-in slide-in-from-bottom duration-300 ${
                adjustment > 0 
                  ? 'bg-red-100 text-red-700 border-2 border-red-300' 
                  : 'bg-green-100 text-green-700 border-2 border-green-300'
              }`}>
                {adjustment > 0 
                  ? `⏱️ Delivery will be delayed by ${adjustment} minutes` 
                  : `⚡ Delivery will be advanced by ${Math.abs(adjustment)} minutes`
                }
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="flex-1 px-4 py-2.5 rounded-lg border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={adjustment === 0 || isUpdating}
            className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isUpdating ? (
              <>
                <LoadingOutlined className="animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <ClockCircleOutlined />
                Apply Time Change
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}